<template>
    <div class="expandable-color-picker" :class="{inline: type=='inline'}">
        <h4 v-if="type=='column'">{{title}}</h4>
        <label v-if="type=='inline'" @click="toggle">{{title}}</label>
        <button class="action-expand" :aria-label="label" :title="label" @click="toggle" :class="{expanded: isExpanded}">{{isExpanded? '-' : '+'}}</button>
        <div class="static-color" v-if="!isExpanded" :style="{background: value, color: complementaryColor}" @click="expand">{{value}}</div>
        <Chrome v-else :value="value" :disableAlpha="true" @input="onChange"/>
    </div>
</template>

<script>
import { Chrome } from "vue-color";

function hexToRGB(hex) {
  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error("Invalid HEX color.");
  }
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16)
  ];
}

function invertColor(hex) {
  const rgb = hexToRGB(hex);
  const r0 = rgb[0];
  const g0 = rgb[1];
  const b0 = rgb[2];
  // invert color components
  const r = (255 - r0).toString(16),
    g = (255 - g0).toString(16),
    b = (255 - b0).toString(16);

  // pad each with zeros and return
  return "#" + padZero(r) + padZero(g) + padZero(b);
}

function getColorValue(nr) {
  const v = nr / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function getLuminance(rgb) {
  return (
    getColorValue(rgb[0]) * 0.2126 +
    getColorValue(rgb[1]) * 0.7152 +
    getColorValue(rgb[2]) * 0.0722
  );
}

function getContrast(c1, c2) {
  const rgb1 = hexToRGB(c1);
  const rgb2 = hexToRGB(c2);
  const l1 = getLuminance(rgb1) + 0.05;
  const l2 = getLuminance(rgb2) + 0.05;
  return l1 > l2 ? l1 / l2 : l2 / l1;
}

function padZero(str, len) {
  len = len || 2;
  var zeros = new Array(len).join("0");
  return (zeros + str).slice(-len);
}

export default {
  name: "ExpandableColorPicker",
  components: {
    Chrome
  },
  props: {
    onChange: Function,
    value: {
      type: String,
      default: "#4a4f62"
    },
    title: String,
    type: {
      type: String,
      default: "column"
    },
    onExpand: Function
  },
  data() {
    return {
      isExpanded: false,
      hasUpdateBeenCalled: false
    };
  },
  updated(){
    var inputs = window.document.querySelectorAll(".vc-input__input:not([data-initialized='true'])");
    for(let input of inputs){
      input.dataset['initialized'] = 'true';
      input.addEventListener("keydown", function(e){
        if(e.keyCode===13){
          this.close();
        }
      }.bind(this));
    }
  },
  computed: {
    label() {
      return this.isExpanded ? "Hide" : "Expand";
    },
    complementaryColor() {
      const colors = ["#fff", "#4a4f62", "#000"];
      for (let color of colors) {
        if (getContrast(this.value, color) >= 3) {
          return color;
        }
      }
      return invertColor(this.value);
    }
  },
  methods: {
    dispatchCloseEvent(){
      var e = undefined;
      if (window.CustomEvent) {
        try {
          e = new CustomEvent("closeColorPicker");
        } catch (ex) {}
      }
      if (e === undefined) {
        e = this.contentDocument.createEvent("CustomEvent");
        e.initCustomEvent("closeColorPicker", true, true, {});
      }
      window.dispatchEvent(e);
    },
    expand() {
      this.dispatchCloseEvent();
      this.isExpanded = true;
    },
    toggle() {
      if(!this.isExpanded){
        this.dispatchCloseEvent();
      }
      this.isExpanded = !this.isExpanded;
    },
    close() {
      this.isExpanded = false;
    }
  },
  mounted() {
    window.addEventListener(
      "closeColorPicker",
      function(e) {
        this.close();
      }.bind(this)
    );
  }
};
</script>

<style>
.expandable-color-picker {
  position: relative;
}

.expandable-color-picker .action-expand {
  position: absolute;
  top: 1.33em;
  right: 10px;
  font-size: 1em;
  font-weight: 500;
  background: #fff;
  color: #c64624;
  border: 1px solid #c64624;
  box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.12);
  border-radius: 3px;
  padding: 0 5px;
  cursor: pointer;
  transition: color 0.3s ease-in-out, background-color 0.3s ease-in-out;
}

.expandable-color-picker .action-expand.expanded {
  padding: 0 7px 2px 7px;
  top: -52px;
}

.expandable-color-picker .action-expand:hover {
  background: #c64624;
  color: #fff;
}

.expandable-color-picker .static-color {
  width: 225px;
  height: 50px;
  cursor: pointer;
  border-radius: 3px;
  box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
}

.expandable-color-picker.inline {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  margin: 0.5em 0;
}

.expandable-color-picker.inline .action-expand {
  display: none;
}

.expandable-color-picker label {
  transition: all 0.3s ease-in-out;
}

.expandable-color-picker label:hover {
  color: #4a4f62;
  cursor: pointer;
}
</style>


// WEBPACK FOOTER //
// src/components/ExpandableColorPicker.vue